import { useRouter } from 'next/router';
import type { Dispatch, FC, SetStateAction } from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { PAGE_IDS } from '@constants/docs';

type ContextValue = { hash: string; isHashScrolling: boolean; setHashScrolling: Dispatch<SetStateAction<boolean>> };

export const useUrlHashContext = () => useContext(UrlHashContext);

export const UrlHashContext = createContext<ContextValue>({
  hash: '',
  isHashScrolling: false,
  setHashScrolling: () => {},
});

export const UrlHashProvider: FC<{ children?: React.ReactNode }> = ({ children }) => {
  const router = useRouter();
  const [hash, setHash] = useState('');
  const [isHashScrolling, setHashScrolling] = useState<boolean>(false);

  const updateHash = (hash: string) => {
    if (!hash) {
      return;
    }

    setHash(hash.split('#')[1]);
  };

  useEffect(() => {
    const onWindowHashChange = () => {
      const urlHash = window.location.hash;
      const pathSlug = window.location.pathname.split('/');

      // Check redirect url to glossary page
      if (pathSlug[pathSlug.length - 1] === PAGE_IDS.GLOSSARY) {
        // Check for wrong order of hash and query params: some/url#hash?term=someTerm <- wrong
        const hashAndQueryRegExp = /(#.+?)(\?term=.+?)$/;
        const match = urlHash.match(hashAndQueryRegExp);
        if (match) {
          // Place hash and query in correct order: some/url?term=someTerm#hash <- correct
          // toLowerCase we need because in some reason hash in DPv1 can be in different cases for the same letter: #P and #p
          const queryAndHashInCorrectOrder = match[2] + match[1].toLowerCase();
          router.push(router.asPath.replace(hashAndQueryRegExp, queryAndHashInCorrectOrder));
          return;
        }
      }
      updateHash(urlHash);
    };

    if (router.isReady) {
      onWindowHashChange();
    }

    const onNextJSHashChange = (url: string) => updateHash(url);

    router.events.on('routeChangeStart', onNextJSHashChange);
    window.addEventListener('hashchange', onWindowHashChange);
    window.addEventListener('load', onWindowHashChange);

    return () => {
      router.events.off('routeChangeStart', onNextJSHashChange);
      window.removeEventListener('load', onWindowHashChange);
      window.removeEventListener('hashchange', onWindowHashChange);
    };
  }, [router, router.asPath, router.events]);

  const contextValue = useMemo(() => ({ hash, isHashScrolling, setHashScrolling }), [hash, isHashScrolling]);

  return <UrlHashContext.Provider value={contextValue}>{children}</UrlHashContext.Provider>;
};
